import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Save, Trash2, Sparkles } from 'lucide-react';
import {
  tabletopExercisesAPI,
  TabletopExercise,
  TabletopFrameworkPackId,
} from '../../services/tabletopService';
import GenerationProgressPage from './GenerationProgressPage';
import ScenarioReviewPage from './ScenarioReviewPage';
import CastSetupPage from './CastSetupPage';
import RunConsolePage from './RunConsolePage';
import ResultsPage from './ResultsPage';

const FRAMEWORK_PACK_OPTIONS: { id: TabletopFrameworkPackId; label: string; description: string }[] = [
  { id: 'csf-2.0', label: 'NIST CSF 2.0', description: 'Detect, Respond, Recover and governance/supply-chain evidence.' },
  { id: 'uk-caf', label: 'UK Cyber Assessment Framework', description: 'Objective-level skeleton; detailed mapping lands in a later phase.' },
];

const isNew = (exerciseId?: string) => !exerciseId || exerciseId === 'new';

const ExerciseSeedPage: React.FC = () => {
  const { projectId, exerciseId } = useParams<{ projectId: string; exerciseId: string }>();
  const navigate = useNavigate();
  const creating = isNew(exerciseId);

  const [loading, setLoading] = useState(!creating);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [exercise, setExercise] = useState<TabletopExercise | null>(null);

  const [seed, setSeed] = useState('');
  const [sector, setSector] = useState('');
  const [sizeDescription, setSizeDescription] = useState('');
  const [jurisdiction, setJurisdiction] = useState('');
  const [thirdPartyDependencies, setThirdPartyDependencies] = useState('');
  const [frameworkPacks, setFrameworkPacks] = useState<TabletopFrameworkPackId[]>(['csf-2.0']);

  useEffect(() => {
    if (creating || !projectId || !exerciseId) return;
    tabletopExercisesAPI.get(projectId, exerciseId)
      .then(r => {
        const ex = r.data.exercise;
        setExercise(ex);
        setSeed(ex.seed);
        setSector(ex.orgProfile.sector || '');
        setSizeDescription(ex.orgProfile.sizeDescription || '');
        setJurisdiction(ex.orgProfile.jurisdiction || '');
        setThirdPartyDependencies((ex.orgProfile.thirdPartyDependencies || []).join(', '));
        setFrameworkPacks(ex.frameworkPacks);
      })
      .finally(() => setLoading(false));
  }, [projectId, exerciseId, creating]);

  const togglePack = (id: TabletopFrameworkPackId) => {
    setFrameworkPacks(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]);
  };

  const handleSave = async () => {
    if (!projectId) return;
    setSaving(true);
    try {
      const orgProfile = {
        sector,
        sizeDescription: sizeDescription || undefined,
        jurisdiction: jurisdiction || undefined,
        thirdPartyDependencies: thirdPartyDependencies.split(',').map(s => s.trim()).filter(Boolean),
      };
      if (creating) {
        const r = await tabletopExercisesAPI.create(projectId, { seed, orgProfile, frameworkPacks });
        navigate(`/projects/${projectId}/tabletop/exercises/${r.data.exercise.exerciseId}`, { replace: true });
      } else if (exerciseId) {
        const r = await tabletopExercisesAPI.update(projectId, exerciseId, { seed, orgProfile, frameworkPacks });
        setExercise(r.data.exercise);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!projectId || !exerciseId || creating) return;
    if (!window.confirm('Delete this exercise? This cannot be undone.')) return;
    await tabletopExercisesAPI.remove(projectId, exerciseId);
    navigate(`/projects/${projectId}/tabletop`);
  };

  const handleGenerate = async () => {
    if (!projectId || !exerciseId) return;
    setGenerating(true);
    try {
      const r = await tabletopExercisesAPI.generate(projectId, exerciseId);
      setExercise(r.data.exercise);
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 size={22} className="animate-spin text-slate-400" />
      </div>
    );
  }

  if (exercise && exercise.status === 'generating') {
    return <GenerationProgressPage projectId={projectId!} exercise={exercise} onAdvance={setExercise} />;
  }

  if (exercise && exercise.status === 'generated') {
    return <ScenarioReviewPage projectId={projectId!} exercise={exercise} onExerciseChange={setExercise} />;
  }

  if (exercise && exercise.status === 'approved') {
    return <CastSetupPage projectId={projectId!} exercise={exercise} onExerciseChange={setExercise} />;
  }

  if (exercise && exercise.status === 'running') {
    return <RunConsolePage projectId={projectId!} exercise={exercise} onExerciseChange={setExercise} />;
  }

  if (exercise && exercise.status === 'complete') {
    return <ResultsPage projectId={projectId!} exercise={exercise} />;
  }

  const canSave = seed.trim().length > 0 && sector.trim().length > 0;

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[800px] mx-auto flex flex-col gap-6">

        <header className="flex items-center justify-between">
          <button
            onClick={() => navigate(`/projects/${projectId}/tabletop`)}
            className="flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-navy transition-colors"
          >
            <ArrowLeft size={16} /> Back to exercises
          </button>
          {!creating && exercise?.status === 'draft' && (
            <button
              onClick={handleDelete}
              className="flex items-center gap-2 text-sm font-semibold text-red-500 hover:text-red-700 transition-colors"
            >
              <Trash2 size={15} /> Delete
            </button>
          )}
        </header>

        <div>
          <h1 className="text-2xl font-bold tracking-tight text-navy">
            {creating ? 'New Exercise' : 'Exercise Seed'}
          </h1>
          <p className="text-slate-500 text-sm font-medium mt-1">
            {creating
              ? 'Give the build pipeline a one-line seed and enough organisation context to cast a credible roster.'
              : exercise?.status === 'draft'
                ? 'Draft — edit the seed and organisation profile before generating the scenario package.'
                : 'This exercise has moved past the draft stage; the seed is now read-only.'}
          </p>
        </div>

        <div className="bg-white border border-[#e2e8f0] rounded-2xl p-6 flex flex-col gap-5">
          <div>
            <label className="text-[11px] font-bold text-primary tracking-widest uppercase">Seed</label>
            <textarea
              value={seed}
              onChange={e => setSeed(e.target.value)}
              disabled={!creating && exercise?.status !== 'draft'}
              rows={3}
              placeholder='e.g. "Ransomware hits our children&apos;s services case management system via our outsourced IT provider"'
              className="mt-1.5 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-navy focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:bg-slate-50 disabled:text-slate-400"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[11px] font-bold text-primary tracking-widest uppercase">Sector</label>
              <input
                value={sector}
                onChange={e => setSector(e.target.value)}
                disabled={!creating && exercise?.status !== 'draft'}
                placeholder="e.g. UK county council"
                className="mt-1.5 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-navy focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:bg-slate-50 disabled:text-slate-400"
              />
            </div>
            <div>
              <label className="text-[11px] font-bold text-primary tracking-widest uppercase">Jurisdiction</label>
              <input
                value={jurisdiction}
                onChange={e => setJurisdiction(e.target.value)}
                disabled={!creating && exercise?.status !== 'draft'}
                placeholder="e.g. England"
                className="mt-1.5 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-navy focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:bg-slate-50 disabled:text-slate-400"
              />
            </div>
          </div>

          <div>
            <label className="text-[11px] font-bold text-primary tracking-widest uppercase">Organisation size</label>
            <input
              value={sizeDescription}
              onChange={e => setSizeDescription(e.target.value)}
              disabled={!creating && exercise?.status !== 'draft'}
              placeholder="e.g. ~4,000 staff, single-tier authority"
              className="mt-1.5 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-navy focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:bg-slate-50 disabled:text-slate-400"
            />
          </div>

          <div>
            <label className="text-[11px] font-bold text-primary tracking-widest uppercase">Third-party dependencies</label>
            <p className="text-xs text-slate-400 mt-0.5 mb-1.5">
              Comma-separated. This is usually where the exercise's real tension lives — outsourced IT, SOC/MDR, cloud platform team, etc.
            </p>
            <input
              value={thirdPartyDependencies}
              onChange={e => setThirdPartyDependencies(e.target.value)}
              disabled={!creating && exercise?.status !== 'draft'}
              placeholder="outsourced IT, SOC/MDR provider"
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-navy focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:bg-slate-50 disabled:text-slate-400"
            />
          </div>

          <div>
            <label className="text-[11px] font-bold text-primary tracking-widest uppercase">Compliance frameworks</label>
            <div className="mt-2 flex flex-col gap-2">
              {FRAMEWORK_PACK_OPTIONS.map(opt => (
                <label key={opt.id} className="flex items-start gap-3 p-3 rounded-xl border border-slate-200 cursor-pointer hover:bg-slate-50">
                  <input
                    type="checkbox"
                    checked={frameworkPacks.includes(opt.id)}
                    onChange={() => togglePack(opt.id)}
                    className="mt-0.5"
                  />
                  <div>
                    <p className="text-sm font-semibold text-navy">{opt.label}</p>
                    <p className="text-xs text-slate-500">{opt.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <button
            onClick={handleSave}
            disabled={!canSave || saving}
            className="flex items-center gap-2 rounded-xl h-10 px-5 bg-white border border-slate-200 text-slate-600 text-sm font-bold hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed shadow-sm transition-colors"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            {creating ? 'Create Exercise' : 'Save Changes'}
          </button>
          {!creating && exercise?.status === 'draft' && (
            <button
              onClick={handleGenerate}
              disabled={!canSave || generating}
              className="flex items-center gap-2 rounded-xl h-10 px-5 bg-primary text-white text-sm font-bold hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed shadow-sm transition-opacity"
            >
              {generating ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
              Generate Scenario
            </button>
          )}
        </div>

      </div>
    </div>
  );
};

export default ExerciseSeedPage;
